import React, { useState } from 'react';
import { connect } from '../../redux/store';
import { Link } from '@reach/router';
import { UncontrolledTooltip } from 'reactstrap';
import { CardCol, CardRow, CardCorner, C } from '@kineticdata/bundle-common';
import { ApprovalMetaWrapper } from './ApprovalMetaWrapper';
import { ApprovalModal } from './Approval';
import * as helpers from '../../utils';
import { Form } from '../../models';
import { I18n } from '@kineticdata/react';

export const ApprovalCard = connect(state => ({
  profile: state.app.profile,
  mobile: state.app.layoutSize === 'small',
}))(props => {
  const { submission, path, components = {} } = props;
  const CardTag = components.Link || Link;
  const form = Form(submission.form);
  const color = helpers.getActionStatusColor(submission);

  // State for approval modal
  const [decision, setDecision] = useState(null);
  const approve = e => {
    e.preventDefault();
    setDecision('approve');
  };
  const deny = e => {
    e.preventDefault();
    setDecision('deny');
  };
  const reset = () => setDecision(null);

  return (
    <>
      <ApprovalMetaWrapper submission={submission}>
        {meta => (
          <CardTag className="card" to={path}>
            {meta.isLocked &&
              !meta.isLockedByMe && (
                <>
                  <CardCorner
                    id={`lock1-${submission.id}`}
                    className="card__corner--danger"
                    tag="span"
                    icon="fa fa-lock"
                  />
                  <UncontrolledTooltip
                    placement="right"
                    target={`lock1-${submission.id}`}
                    delay={0}
                    trigger="hover focus"
                  >
                    <I18n
                      render={translate =>
                        translate(
                          `This approval is currently locked by another user. Lock expires in ~%d minute%p.`,
                        )
                          .replace('%d', meta.lockMinutesLeft)
                          .replace('%p', meta.lockMinutesLeft !== 1 ? 's' : '')
                      }
                    />
                  </UncontrolledTooltip>
                </>
              )}
            <CardRow>
              <CardRow type="prepend" className="text-muted">
                <span className={`fa fa-fw fa-2x fa-${form.icon}`} />
              </CardRow>

              <CardCol>
                <CardRow className="text-muted">
                  <span>
                    <strong>
                      <I18n>
                        {submission.values[C.FIELD_REQUEST_FORM_NAME] ||
                          form.name}
                      </I18n>
                    </strong>
                    {!!submission.values[C.FIELD_REQUEST_SUBMISSION_ID] && (
                      <>
                        {' \u00b7 '}
                        {submission.values[C.FIELD_REQUEST_SUBMISSION_ID]
                          .slice(-6)
                          .toUpperCase()}
                      </>
                    )}
                  </span>
                </CardRow>
                <CardRow type="subtitle">
                  <I18n>
                    {submission.values[C.FIELD_APPROVAL_SUMMARY] ||
                      submission.label}
                  </I18n>
                </CardRow>
                {!!submission.values[C.FIELD_APPROVAL_DETAILS] && (
                  <CardRow className="text-muted mb-1">
                    <I18n>{submission.values[C.FIELD_APPROVAL_DETAILS]}</I18n>
                  </CardRow>
                )}
                <CardRow type="meta" className="text-muted">
                  <dl className="flat">
                    {[
                      {
                        value: (
                          <span
                            className={`badge badge-${color} badge-stylized text-muted`}
                          >
                            <I18n>{helpers.getStatus(submission)}</I18n>
                          </span>
                        ),
                      },
                      { label: 'Created', value: meta.showCreatedDate() },
                      { label: 'Submitter', value: meta.showSubmitter() },
                      { label: 'Completed', value: meta.showCompletedDate() },
                      {
                        label: 'Approvals Received',
                        value: meta.showApprovalsCount(),
                      },
                      { value: meta.showIssues() },
                    ]
                      .filter(m => !!m.value)
                      .map(({ label, value }, i) => (
                        <div key={`meta-${i}`}>
                          {label && (
                            <dt>
                              <I18n>{label}</I18n>
                            </dt>
                          )}
                          {value && <dd>{value}</dd>}
                        </div>
                      ))}
                  </dl>
                </CardRow>
              </CardCol>

              {submission.coreState === C.CORE_STATE_DRAFT &&
                !props.mobile &&
                (!meta.isCompletedByMe ? (
                  <CardRow type="append">
                    <button
                      className="btn btn-icon-info px-2 card-icon-action"
                      onClick={approve}
                      title="Approve"
                    >
                      <span className="fa fa-fw fa-lg fa-check-circle" />
                    </button>
                    <button
                      className="btn btn-icon-danger px-2 card-icon-action"
                      onClick={deny}
                      title="Deny"
                    >
                      <span className="fa fa-fw fa-lg fa-times-circle" />
                    </button>
                  </CardRow>
                ) : (
                  <CardRow type="append">
                    <span
                      className="btn btn-icon-dark px-2"
                      aria-label="You have completed this approval. Awaiting approval from others."
                      id={`approved-${submission.id}`}
                    >
                      <span className="fa fa-fw fa-lg fa-info-circle" />
                    </span>
                    <UncontrolledTooltip
                      placement="left"
                      target={`approved-${submission.id}`}
                      delay={0}
                      trigger="hover focus"
                    >
                      <I18n>
                        You have completed this approval. Awaiting approval from
                        others.
                      </I18n>
                    </UncontrolledTooltip>
                  </CardRow>
                ))}
            </CardRow>
          </CardTag>
        )}
      </ApprovalMetaWrapper>
      {decision !== null && (
        <ApprovalModal
          submissionId={submission.id}
          toggle={reset}
          isApproved={decision === 'approve'}
          completed={() => {
            reset();
            if (typeof props.reload === 'function') {
              props.reload();
            }
          }}
        />
      )}
    </>
  );
});
